import { API } from '../shared/constants';
import { HttpConnectorAdapter } from './http-connector-adapter';
import { AdminBaseAdapter } from './admin-base-adapter';
import { LeafletPDFData } from '../shared/domain/leaflet';
import { JobRequest, JobResult, JobScheduleResult } from '../models/admin-jobs';
import { JobErrorResult, JobStatusResult } from '../models/jobs';
import { QRTemplateResponse } from '../models/admin';

let singletonInstance: AdminAdapter;
export class AdminAdapter implements AdminBaseAdapter {
    static getAdapter(): AdminBaseAdapter {
        if (!singletonInstance) {
            singletonInstance = new AdminAdapter();
        }

        return singletonInstance;
    }

    async getTeamsByAccountId(accountId: string): Promise<{team_id: string, name: string}[]> {
        const {
            ADMIN_TOOL: { TEAMS_BY_ACCOUNT },
            Tokens: { ACCOUNTS: { ACCOUNT } }
        } = API.URL;

        const response = await HttpConnectorAdapter
            .get(TEAMS_BY_ACCOUNT.replace(ACCOUNT, accountId), {}, {authToken: ''});

        const { 
            status: responseStatus,
            data
        } : {
            status: number,
            data: {team_id: string, name: string}[]
        } = response;

        if ((responseStatus !== 200) || !data) {
            return Promise.reject();
        }

        return data.map((team: {team_id: string, name: string}) => {
            return {
                team_id: team.team_id,
                name: team.name
            };
        });
    }

    async getUserByEmail(accountId: string, email: string): Promise<{userId: string, username: string, teams: {team_id: string, name?: string}[]}> {
        const {
            ADMIN_TOOL: { USER_BY_EMAIL },
            Tokens: { ACCOUNTS: { ACCOUNT } }
        } = API.URL;

        const response = await HttpConnectorAdapter
            .get(`${USER_BY_EMAIL.replace(ACCOUNT, accountId)}${email}`, {}, {authToken: ''});

        const {
            results: { users }
        } : {
            results: {
                users: {
                    user_id: string,
                    username: string,
                    teams: {team_id: string, name?: string}[]
                }[]
            }
        } = response.data;

        if (!users || users.length === 0) {
            return Promise.reject();
        }

        const { username, user_id, teams } = users[0];
        return {
            userId: user_id,
            username,
            teams
        };
    }

    async getTabsByUserId(userId: string, teamId: string): Promise<string[]> {
        const {
            ADMIN_TOOL: { TABS_BY_USER },
            Tokens: { USERS: { USER_ID } }
        } = API.URL;

        const response = await HttpConnectorAdapter
            .get(TABS_BY_USER.replace(USER_ID, userId), {}, {teamId});

        const { 
            status: responseStatus,
            data
        } : {
            status: number,
            data: {
                admin_tabs: string[]
            }
        } = response;

        if ((responseStatus !== 200) || !data) {
            return Promise.reject();
        }

        const { admin_tabs: adminTabs } = data;

        return adminTabs;
    }

    async getLeafletData(floorplanId: string): Promise<LeafletPDFData> {
        const {
            ADMIN_TOOL: { LEAFLET_DATA },
            Tokens: { MAPS: { FLOORPLAN_ID } }
        } = API.URL;

        const response = await HttpConnectorAdapter
            .get(LEAFLET_DATA.replace(FLOORPLAN_ID, floorplanId), {}, {authToken: ''});

        const {
            status: responseStatus,
            data
        } : {
            status: number,
            data: LeafletPDFData
        } = response;

        if ((responseStatus !== 200) || !data) {
            return Promise.reject();
        }

        return data;
    }

    async uploadQRCodeFiles(qrPositionFile: File, qrTemplateFile: File): Promise<QRTemplateResponse> {
        const {
            ADMIN_TOOL: { QR_CODE_TEMPLATES }
        } = API.URL;

        const formData = new FormData();
        formData.append('position_file', qrPositionFile);
        formData.append('template_file', qrTemplateFile);

        const response = await HttpConnectorAdapter
            .post(QR_CODE_TEMPLATES, formData, {authToken: ''});

        const {
            status: responseStatus,
            data
        } = response;

        if ((responseStatus !== 200) || !data) {
            return Promise.reject();
        }

        return data as QRTemplateResponse;
    }

    async submitAdminJob(request: JobRequest): Promise<JobScheduleResult> {
        const formData = new FormData();
        formData.append('job_type', request.job_type);
        formData.append('user_created_id', request.user_created_id);
        formData.append('account_id', request.account_id);
        formData.append('additional_params', JSON.stringify(request.additional_params));

        const {
            ADMIN_TOOL: { JOBS }
        } = API.URL;

        const response = await HttpConnectorAdapter
            .post(JOBS, formData, {authToken: ''});

        const {
            status: responseStatus,
            data
        } = response;

        if ((responseStatus !== 200) || !data) {
            return Promise.reject();
        }

        const {
            job_id: jobId,
            error,
            stack_trace: strackTrace
        } = data;

        return {
            status: responseStatus,
            jobId,
            error,
            strackTrace
        } as JobScheduleResult;
    }

    async getJobStatus(jobId: string): Promise<JobStatusResult> {
        const {
            ADMIN_TOOL: { JOBS }
        } = API.URL;
        
        const response = await HttpConnectorAdapter
            .get(`${JOBS}/${jobId}/status`, {}, {authToken: ''});

        const {
            status: responseStatus,
            data
        } = response;

        if ((responseStatus !== 200) || !data) {
            return Promise.reject();
        }

        const {
            job_id,
            job_status,
            estimate_percent_complete,
        } = data;

        return {
            status: job_status,
            jobId: job_id,
            completion: estimate_percent_complete
        } as JobStatusResult;
    }

    async getJobError(jobId: string): Promise<JobErrorResult> {
        const {
            ADMIN_TOOL: { JOBS }
        } = API.URL;
        
        const response = await HttpConnectorAdapter
            .get(`${JOBS}/${jobId}/error`, {}, {authToken: ''});

        const { 
            status: responseStatus,
            data
        } : {
            status: boolean,
            data?: JobErrorResult
        } = response.data;

        if ((!responseStatus) || !data) {
            return Promise.reject();
        }
        return Promise.resolve(data);
    }

    async getJobResult(jobId: string): Promise<JobResult> {
        const {
            ADMIN_TOOL: { JOBS }
        } = API.URL;
        
        const response = await HttpConnectorAdapter
            .get(`${JOBS}/${jobId}/results`, {}, {authToken: ''});


        const {
            status: responseStatus,
            data
        } = response;

        if ((!responseStatus) || !data) {
            return Promise.reject();
        }

        return Promise.resolve(data as JobResult);
    }
}
